import ReplyIcon from '@mui/icons-material/Reply';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Box, Button, Card, Checkbox, Chip, LinearProgress, Tooltip, Typography } from '@mui/material';
import FileTypeIcon, { fileTypeForContent } from '../common/FileTypeIcon';
import { categoryLabel, formatDuration, useTaskCategories } from './taskFields';
import TaskStatusChip from './TaskStatusChip';
import { attentionLabel, displayStatus, formatDue, reasonLabel, rowAssignee, rowDue, SEND_BACK_REASONS, shortName } from './taskUi';

// Column widths for the task list; the header in ListView uses the same table
export const TASK_COLUMN_WIDTHS = {
    title: { xs: '100%', md: 3.2 },
    language: 1,
    stage: 1.6,
    status: 1.4,
    assignee: 1.4,
    due: 1,
    posting: 1,
    category: 1.2,
    pm: 1.3,
    progress: 1.2,
    action: 1.3,
    reasons: 2.2,
    sentBack: 2.6,
};

export const TASK_COLUMN_LABELS = {
    title: 'File',
    language: 'Language',
    stage: 'Stage',
    status: 'Status',
    assignee: 'Assignee',
    due: 'Due',
    posting: 'Posting date',
    category: 'Category',
    pm: 'PM',
    progress: 'Progress',
    action: '',
    reasons: 'Why',
    sentBack: 'Sent back',
};

const flexFor = (key) => {
    const w = TASK_COLUMN_WIDTHS[key];
    return typeof w === 'number' ? `${w} 1 0` : `${w.md} 1 0`;
};

/** A cell in the task row; wraps under the title on narrow screens. */
const Cell = ({ col, children }) => (
    <Box
        sx={{
            flex: { xs: '0 0 auto', md: flexFor(col) },
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            pr: 1,
        }}
    >
        {children}
    </Box>
);

/**
 * One row per (file, language) in a task view. Columns come from the view's schema.
 * Clicking the row opens the task drawer. Stages are one step (no Start): the action is
 * Complete (opens the drawer at the handoff step), or Open in editor for a stage done in the
 * editor, which starts it. With onSelectToggle (select mode) clicking the row selects it instead.
 */
const TaskListItem = ({ row, columns, me, onOpen, selected, onSelectToggle }) => {
    const activate = () => (onSelectToggle ? onSelectToggle(row.id) : onOpen(row));
    const status = displayStatus(row);
    const categories = useTaskCategories();
    // Up-next rows are about a later stage; nothing to do there yet
    const isMine = me && row.currentStageAssignee === me && !row.extras?.upNext;
    const actionable =
        isMine && ['ASSIGNED', 'IN_PROGRESS'].includes(row.currentStageStatus) && row.overallStatus !== 'ON_HOLD';
    const canComplete = actionable && !row.currentStageAuto && row.contentType !== 'POST';
    const canOpenEditor = actionable && row.currentStageAuto;
    const extras = row.extras || {};

    const render = (col) => {
        switch (col) {
            case 'title':
                return null; // rendered separately
            case 'language':
                return <Chip size="small" label={row.languageLabel} sx={{ fontSize: '0.72rem' }} />;
            case 'stage':
                return (
                    <Typography variant="body2" noWrap title={extras.upNext ? extras.upNextStageName : row.currentStageName}>
                        {extras.upNext ? `Next: ${extras.upNextStageName || ''}` : row.currentStageName || 'Done'}
                    </Typography>
                );
            case 'status':
                return <TaskStatusChip row={row} />;
            case 'assignee': {
                const who = rowAssignee(row);
                // Nobody is on a waiting task yet, and that's not a problem
                if (row.waitingForFile) return null;
                const missing = extras.upNext ? 'Not planned' : 'Unassigned';
                return (
                    <Typography
                        variant="body2"
                        noWrap
                        color={who ? 'text.primary' : extras.upNext ? 'text.secondary' : 'warning.main'}
                    >
                        {row.redacted ? '' : who ? shortName(who) : missing}
                    </Typography>
                );
            }
            case 'due': {
                const due = rowDue(row);
                return (
                    <Typography
                        variant="body2"
                        noWrap
                        color={status === 'OVERDUE' ? 'error.main' : status === 'DUE_SOON' ? 'warning.main' : 'text.secondary'}
                    >
                        {due ? `due ${formatDue(due)}` : ''}
                    </Typography>
                );
            }
            case 'posting':
                return (
                    <Typography variant="body2" noWrap color="text.secondary">
                        {row.targetDate ? formatDue(row.targetDate) : ''}
                    </Typography>
                );
            case 'category':
                return (
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" noWrap>
                            {categoryLabel(categories, row.categoryKey)}
                        </Typography>
                        {row.durationSeconds > 0 && (
                            <Typography variant="caption" color="text.secondary" noWrap component="div">
                                {formatDuration(row.durationSeconds)}
                            </Typography>
                        )}
                    </Box>
                );
            case 'pm':
                return (
                    <Typography variant="body2" noWrap color="text.secondary">
                        {shortName(row.projectManager)}
                    </Typography>
                );
            case 'progress':
                return (
                    <Tooltip title={`${row.percentComplete}% of stages done`}>
                        <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LinearProgress
                                variant="determinate"
                                value={row.percentComplete}
                                sx={{ flex: 1, height: 6, borderRadius: 3 }}
                                aria-label="progress"
                            />
                            <Typography variant="caption" color="text.secondary">
                                {row.percentComplete}%
                            </Typography>
                        </Box>
                    </Tooltip>
                );
            case 'reasons':
                return (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(extras.reasons || row.attentionReasons || []).map((r) => (
                            <Chip
                                key={r}
                                size="small"
                                color="warning"
                                variant="outlined"
                                label={attentionLabel(r, extras, row)}
                                sx={{ fontSize: '0.7rem' }}
                            />
                        ))}
                    </Box>
                );
            case 'sentBack':
                return (
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" noWrap>
                            <ReplyIcon sx={{ fontSize: '0.9rem', verticalAlign: 'text-bottom', mr: 0.5 }} />
                            {extras.sentBackReason ? reasonLabel(SEND_BACK_REASONS, extras.sentBackReason) : 'Changes asked for'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap title={extras.sentBackNote}>
                            {extras.sentBackNote}
                        </Typography>
                    </Box>
                );
            case 'action':
                if (onSelectToggle) return null;
                if (canOpenEditor) {
                    return (
                        <Button
                            size="small"
                            variant="contained"
                            startIcon={<OpenInNewIcon />}
                            onClick={(e) => {
                                e.stopPropagation();
                                onOpen(row, { action: 'editor' });
                            }}
                        >
                            Open in editor
                        </Button>
                    );
                }
                if (canComplete) {
                    return (
                        <Button
                            size="small"
                            variant="contained"
                            color="success"
                            onClick={(e) => {
                                e.stopPropagation();
                                onOpen(row, { action: 'complete' });
                            }}
                        >
                            Complete
                        </Button>
                    );
                }
                return null;
            default:
                return null;
        }
    };

    return (
        <Card
            onClick={activate}
            className="task-list-item"
            role="button"
            tabIndex={0}
            aria-pressed={onSelectToggle ? !!selected : undefined}
            onKeyDown={(e) => {
                if (e.key === 'Enter') activate();
            }}
            sx={{
                p: 1,
                cursor: 'pointer',
                transition: 'box-shadow 0.15s ease-in-out',
                borderLeft: '3px solid',
                borderLeftColor: status === 'BLOCKED' || status === 'OVERDUE' ? 'error.main' : 'transparent',
                backgroundColor: selected ? 'action.selected' : undefined,
                '&:hover': { boxShadow: 3 },
            }}
        >
            <Box sx={{ display: 'flex', flexWrap: { xs: 'wrap', md: 'nowrap' }, alignItems: 'center', rowGap: 0.75, px: 1 }}>
                <Box
                    sx={{
                        flex: { xs: '1 1 100%', md: flexFor('title') },
                        minWidth: 0,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        pr: 1,
                    }}
                >
                    {onSelectToggle && (
                        <Checkbox
                            size="small"
                            checked={!!selected}
                            onClick={(e) => e.stopPropagation()}
                            onChange={() => onSelectToggle(row.id)}
                            tabIndex={-1}
                            inputProps={{ 'aria-label': `Select ${row.contentTitle || row.contentId} ${row.languageLabel}` }}
                            sx={{ p: 0.5 }}
                        />
                    )}
                    <FileTypeIcon type={fileTypeForContent(row.contentType)} />
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body1" noWrap title={row.contentTitle}>
                            {row.contentTitle || row.contentId}
                        </Typography>
                        {row.lastActivityNote && !row.redacted && (
                            <Typography variant="caption" color="text.secondary" noWrap component="div" title={row.lastActivityNote}>
                                {row.lastActivityNote}
                            </Typography>
                        )}
                    </Box>
                </Box>
                {columns
                    .filter((c) => c !== 'title')
                    .map((col) => (
                        <Cell key={col} col={col}>
                            {render(col)}
                        </Cell>
                    ))}
            </Box>
        </Card>
    );
};

export default TaskListItem;
